// Utils
import { formatMessage, formatLocalizedString } from '@/utils/i18n';
import {
  ChartItem,
  getDashStyle,
  getLineWidth,
  getReturnOnInvestment,
  getRollingIntervalFromDates,
  getSeriesBeforeAfter,
  MAX_START_DATE,
  trimComparisonItemsData,
} from '@/utils/charts';
import { COMPARISON_ITEM_TYPES, Interval } from '@/utils/constants';
import { getCommonDataPoints } from '@/utils/comparison';
import portfolioUtils from '@/utils/portfolio';
import { ITEM_COLORS } from '@/utils/colors';

// Types
import Dictionary from '@/types/dictionary';
import RollingIntervals from '@/types/rolling-intervals';
import LocalCurrency from '@/types/local-currency';
import { SeriesOptionsType } from 'highcharts';
import ComparisonItemPerformance from '@/types/comparison-item-performance';
import RebalanceFrequency from '@/types/rebalance-frequency';
import { getComparisonItemHoldings } from './items';
import { deepClone } from '../objects';
import { ROLLING_INTERVALS } from '../portfolios';
import Timestamp from '@/types/timestamp';
import LocalizedString from '@/types/localized-string';

export const getGraphData = (
  items: ChartItem[],
  interval: RollingIntervals,
  startDate: Date | null,
  endDate: Date,
  rebalancePeriod?: RebalanceFrequency,
  shortHistory?: boolean,
  ignoreCommonDataPoints: boolean = false,
): ChartItem[] => {
  const trimmedItems = trimComparisonItemsData(items, startDate || MAX_START_DATE, endDate, interval, shortHistory, ignoreCommonDataPoints);

  // TODO -- Show visual cue if we're trimming more than the asked range
  for (const item of trimmedItems) {
    const fees = (item.discretionaryMer || 0) + (item.tax || 0);

    const holdings = getComparisonItemHoldings(item, true);
    item.performance.rolling = portfolioUtils.calculateGrowth(holdings, fees, rebalancePeriod, shortHistory);

    if (item.performanceBase) {
      const holdings = deepClone(item.holdings).map(h => ({ ...h, rolling: h.baseRolling }));
      item.performanceBase.rolling = portfolioUtils.calculateGrowth(holdings, fees, rebalancePeriod, shortHistory);
    }
  }

  return trimmedItems;
};

export const getRollingData = (
  items: ChartItem[],
  interval: RollingIntervals,
  startDate: Date | null,
  endDate: Date,
  rebalancePeriod?: RebalanceFrequency,
  shortHistory?: boolean,
) => {
  const data = getGraphData(items, interval, startDate, endDate, rebalancePeriod, shortHistory);

  return data.map(item => {
    const performanceRollingData = item.performance && item.performance.rolling ? item.performance.rolling[interval] : [];
    const rollingData = performanceRollingData.filter(rolling => {
      const pointDate = new Date(rolling[0]);
      return (!startDate || pointDate >= startDate) && (!endDate || pointDate <= endDate);
    });
    return {
      ...item,
      rolling: rollingData,
    };
  });
};

export const getLongestGraphSerie = (data: SeriesOptionsType[]) => {
  const mergedData = Object.values(
    data.reduce((acc, datum) => {
      if (acc[datum.key]) {
        return {
          ...acc,
          [datum.key]: {
            ...acc[datum.key],
            data: [...acc[datum.key].data, ...datum.data.slice(1)],
          },
        };
      }

      return {
        ...acc,
        [datum.key]: datum,
      };
    }, {} as Dictionary<SeriesOptionsType>),
  ).map(d => d.data);
  return mergedData.slice(1).reduce((current, d) => (d.length > current.length ? d : current), mergedData[0]);
};

const generateTargetRateSeries = (data: SeriesOptionsType[], rate: number, interval: string, capital: number) => {
  const periodicalRate = (1 + rate / 100) ** (1 / (interval === ROLLING_INTERVALS.WEEKLY ? 52 : 12)) - 1;

  const longestSerie = getLongestGraphSerie(data);

  const targetRolling = longestSerie.map((period: number[]) => [period[0], periodicalRate * 100]);

  return {
    data: getReturnOnInvestment(targetRolling, capital),
    color: '#858995',
    name: formatMessage('Charts.Growth.TargetROI'),
    tooltip: {
      pointFormat: '<span style="color:{point.color}">●</span> {series.name} <b style="color:#A4A7B7;">{point.y:.2f}</b><br/>',
    },
    dashStyle: 'LongDash' as const,
    lineWidth: 2,
    type: 'line' as const,
  } as SeriesOptionsType;
};

const getSeriesData = (
  chartItems: ChartItem[],
  capital: number,
  showBaseCurrencies: boolean | undefined,
  shortHistory: boolean | undefined,
  localCurrency: LocalCurrency,
  startDateForShortHistory: Date | null = null,
  startDate: Date | null = null,
  endDate: Date | null = null,
  originalInterval: RollingIntervals,
  targetROI: number | null = null,
  color: string | undefined = undefined,
  rebalancePeriod?: RebalanceFrequency,
  ignoreCommonDataPoints: boolean = false,
  useMonthly: boolean = false,
) => {
  const data = getGraphData(chartItems, originalInterval, startDate, new Date(), rebalancePeriod, shortHistory || false, ignoreCommonDataPoints);
  const interval = getRollingIntervalFromDates(startDate, endDate || new Date(), data, useMonthly, shortHistory);
  // TODO -- Since graph aren't animated upon adding a series, switching points makes the change weird.
  // https://github.com/highcharts/highcharts/issues/4406
  const seriesData: SeriesOptionsType[] = [];

  for (const item of data) {
    const shortItem = shortHistory && item.isShort && (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.BENCHMARK);

    // Show base performance data if reporting in base currency enabled
    if (showBaseCurrencies && item.performanceBase) {
      const commonProps: SeriesOptionsType = getSeriesOptionsType(item, 'BASE', item.currency, color);

      pushItemSeriesData(
        commonProps,
        item,
        seriesData,
        item.performanceBase,
        shortItem,
        false,
        false,
        startDate,
        endDate,
        capital,
        interval,
        startDateForShortHistory,
      );
    }

    // Show CAD performance data if not reporting in base currency or if this is a CAD-converted item
    if (!showBaseCurrencies || item.isFX) {
      const commonProps: SeriesOptionsType = getSeriesOptionsType(item, localCurrency, localCurrency, color);

      pushItemSeriesData(
        commonProps,
        item,
        seriesData,
        item.performance,
        shortItem,
        showBaseCurrencies || false,
        showBaseCurrencies || false,
        startDate,
        endDate,
        capital,
        interval,
        startDateForShortHistory,
      );
    }
  }

  if (seriesData.length && targetROI != null) {
    seriesData.push(generateTargetRateSeries(seriesData, targetROI, interval, capital));
  }

  return seriesData.filter(serie => serie.data.length > 0);
};

export const getSeriesOptionsType = (
  item: { _id: string; type: string; color?: string },
  keySuffix: string,
  currency: string | undefined,
  color?: string,
): SeriesOptionsType => ({
  key: `${item._id}-${keySuffix}`,
  currency,
  itemType: item.type,
  color: color ?? item.color ?? ITEM_COLORS[0],
  type: 'line',
});

const pushItemSeriesData = (
  commonProps: SeriesOptionsType,
  item: ChartItem,
  seriesData: SeriesOptionsType[],
  performance: ComparisonItemPerformance | undefined,
  shortItem: boolean | undefined,
  isFx: boolean,
  lineStyleFx: boolean,
  startDate: Date | null = null,
  endDate: Date | null = null,
  capital: number,
  interval: RollingIntervals,
  startDateForShortHistory: Date | null = null,
) => {
  const performanceRollingData = performance && performance.rolling ? performance.rolling[interval] : [];
  const rollingData = performanceRollingData.filter(rolling => {
    const pointDate = new Date(rolling[0]);
    return (!startDate || pointDate >= startDate) && (!endDate || pointDate <= endDate);
  });
  if (shortItem) {
    const breakingPoint = getCommonDataPoints([item], interval as Interval, startDateForShortHistory || MAX_START_DATE, 'ASC')[interval];
    const splitData = getSeriesBeforeAfter(item, rollingData, (startDateForShortHistory || MAX_START_DATE).getTime(), breakingPoint, isFx);
    const beforeData = getReturnOnInvestment(splitData.before.data, capital);

    seriesData.push({
      ...splitData.before,
      ...commonProps,
      data: beforeData,
      hideFromLegend: true,
    });

    seriesData.push({
      ...splitData.after,
      ...commonProps,
      data: getReturnOnInvestment(splitData.after.data, (beforeData.length && beforeData[beforeData.length - 1][1]) || capital),
    });
  } else {
    seriesData.push({
      ...commonProps,
      data: getReturnOnInvestment(rollingData, capital),
      name: formatLocalizedString(item.name),
      dashStyle: getDashStyle(item, lineStyleFx),
      lineWidth: getLineWidth(item, lineStyleFx),
    });
  }
};

export const getItemSeriesData = (
  rollingData: [Timestamp, number][],
  commonProps: SeriesOptionsType,
  item: { _id: string; type: string; color?: string; name: LocalizedString },
  shortItem: boolean | undefined,
  breakingPoint: number | undefined,
  isFx: boolean,
  lineStyleFx: boolean,
  capital: number,
  startDateForShortHistory: Date | null = null,
): SeriesOptionsType[] => {
  const returnValue = [];
  if (shortItem && breakingPoint) {
    const splitData = getSeriesBeforeAfter(item, rollingData, (startDateForShortHistory || MAX_START_DATE).getTime(), breakingPoint, isFx);
    const beforeData = getReturnOnInvestment(splitData.before.data, capital);

    returnValue.push({
      ...splitData.before,
      ...commonProps,
      data: beforeData,
      hideFromLegend: true,
    });

    returnValue.push({
      ...splitData.after,
      ...commonProps,
      data: getReturnOnInvestment(splitData.after.data, (beforeData.length && beforeData[beforeData.length - 1][1]) || capital),
    });
  } else {
    returnValue.push({
      ...commonProps,
      data: getReturnOnInvestment(rollingData, capital),
      name: formatLocalizedString(item.name),
      dashStyle: getDashStyle(item, lineStyleFx),
      lineWidth: getLineWidth(item, lineStyleFx),
    });
  }
  return returnValue;
};

export default getSeriesData;
